/*
 * Copyright (c) 2025, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import { QueryBuilder } from "@/components/query-builder"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { MultiSelect, type Option } from "@/components/ui/multi-select"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip"
import { TrackerIconImage } from "@/components/ui/tracker-icon"
import { useInstanceCapabilities } from "@/hooks/useInstanceCapabilities"
import { useInstanceMetadata } from "@/hooks/useInstanceMetadata"
import { useInstanceTrackers } from "@/hooks/useInstanceTrackers"
import { useTrackerCustomizations } from "@/hooks/useTrackerCustomizations"
import { useTrackerIcons } from "@/hooks/useTrackerIcons"
import { api } from "@/lib/api"
import { buildCategorySelectOptions } from "@/lib/category-utils"
import { parseTrackerDomains } from "@/lib/utils"
import type {
  ActionConditions,
  Automation,
  AutomationInput,
  AutomationPreviewResult,
  RegexValidationError,
  RuleCondition
} from "@/types"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Folder, Info, Loader2, Plus, X } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { toast } from "sonner"
import { WorkflowPreviewDialog } from "./WorkflowPreviewDialog"

interface WorkflowDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  instanceId: number
  /** Rule to edit, or null to create a new rule */
  rule: Automation | null
  onSuccess?: () => void
}

// Speed units for display - storage is always KiB/s
const SPEED_LIMIT_UNITS = [
  { value: 1, label: "KiB/s" },
  { value: 1024, label: "MiB/s" },
]

type ActionType = "speedLimits" | "shareLimits" | "pause" | "delete" | "tag" | "category"

// Actions that can be combined (Delete must be standalone)
const COMBINABLE_ACTIONS: ActionType[] = ["speedLimits", "shareLimits", "pause", "tag", "category"]

const ACTION_LABELS: Record<ActionType, string> = {
  speedLimits: "Speed limits",
  shareLimits: "Share limits",
  pause: "Pause",
  delete: "Delete",
  tag: "Tag",
  category: "Category",
}

type FormState = {
  name: string
  trackerPattern: string
  trackerDomains: string[]
  applyToAllTrackers: boolean
  enabled: boolean
  sortOrder?: number
  intervalSeconds: number | null // null = use global default (15m)
  // Shared condition for all actions
  actionCondition: RuleCondition | null
  // Multi-action enabled flags
  speedLimitsEnabled: boolean
  shareLimitsEnabled: boolean
  pauseEnabled: boolean
  deleteEnabled: boolean
  tagEnabled: boolean
  categoryEnabled: boolean
  // Speed limits settings
  exprUploadKiB?: number
  exprDownloadKiB?: number
  // Share limits settings
  exprRatioLimit?: number
  exprSeedingTimeMinutes?: number
  // Delete settings
  exprDeleteMode: "delete" | "deleteWithFiles" | "deleteWithFilesPreserveCrossSeeds"
  // Tag action settings
  exprTags: string[]
  exprTagMode: "full" | "add" | "remove"
  exprUseTrackerAsTag: boolean
  exprUseDisplayName: boolean
  // Category action settings
  exprCategory: string
  exprIncludeCrossSeeds: boolean
  exprBlockIfCrossSeedInCategories: string[]
}

const emptyFormState: FormState = {
  name: "",
  trackerPattern: "",
  trackerDomains: [],
  applyToAllTrackers: false,
  enabled: false,
  intervalSeconds: null,
  actionCondition: null,
  speedLimitsEnabled: false,
  shareLimitsEnabled: false,
  pauseEnabled: false,
  deleteEnabled: false,
  tagEnabled: false,
  categoryEnabled: false,
  exprUploadKiB: undefined,
  exprDownloadKiB: undefined,
  exprRatioLimit: undefined,
  exprSeedingTimeMinutes: undefined,
  exprDeleteMode: "deleteWithFilesPreserveCrossSeeds",
  exprTags: [],
  exprTagMode: "full",
  exprUseTrackerAsTag: false,
  exprUseDisplayName: false,
  exprCategory: "",
  exprIncludeCrossSeeds: false,
  exprBlockIfCrossSeedInCategories: [],
}

// Helper to get enabled actions from form state
function getEnabledActions(state: FormState): ActionType[] {
  const actions: ActionType[] = []
  if (state.speedLimitsEnabled) actions.push("speedLimits")
  if (state.shareLimitsEnabled) actions.push("shareLimits")
  if (state.pauseEnabled) actions.push("pause")
  if (state.deleteEnabled) actions.push("delete")
  if (state.tagEnabled) actions.push("tag")
  if (state.categoryEnabled) actions.push("category")
  return actions
}

// Helper to set an action enabled/disabled
function setActionEnabled(action: ActionType, enabled: boolean): Partial<FormState> {
  const key = `${action}Enabled` as keyof FormState
  return { [key]: enabled }
}


export function WorkflowDialog({ open, onOpenChange, instanceId, rule, onSuccess }: WorkflowDialogProps) {
  const queryClient = useQueryClient()
  const [formState, setFormState] = useState<FormState>(emptyFormState)
  const [previewResult, setPreviewResult] = useState<AutomationPreviewResult | null>(null)
  const [previewInput, setPreviewInput] = useState<FormState | null>(null)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [enabledBeforePreview, setEnabledBeforePreview] = useState<boolean | null>(null)
  // Speed limit units - track separately so they persist when value is cleared
  const [uploadSpeedUnit, setUploadSpeedUnit] = useState(1024) // Default MiB/s
  const [downloadSpeedUnit, setDownloadSpeedUnit] = useState(1024) // Default MiB/s
  const [regexErrors, setRegexErrors] = useState<RegexValidationError[]>([])
  const previewPageSize = 25

  const trackersQuery = useInstanceTrackers(instanceId, { enabled: open })
  const { data: trackerCustomizations } = useTrackerCustomizations()
  const { data: trackerIcons } = useTrackerIcons()
  const { data: metadata } = useInstanceMetadata(instanceId)
  const { data: capabilities } = useInstanceCapabilities(instanceId, { enabled: open })
  const supportsTrackerHealth = capabilities?.supportsTrackerHealth ?? true

  // Build category options for the category action dropdown
  const categoryOptions = useMemo(() => {
    if (!metadata?.categories) return []
    const selected = [formState.exprCategory, ...formState.exprBlockIfCrossSeedInCategories].filter(Boolean)
    return buildCategorySelectOptions(metadata.categories, selected)
  }, [metadata?.categories, formState.exprCategory, formState.exprBlockIfCrossSeedInCategories])

  // Build lookup maps from tracker customizations for merging and nicknames
  const trackerCustomizationMaps = useMemo(() => {
    const domainToCustomization = new Map<string, { displayName: string; domains: string[]; id: number }>()
    const secondaryDomains = new Set<string>()

    for (const custom of trackerCustomizations ?? []) {
      const domains = custom.domains
      if (domains.length === 0) continue

      for (let i = 0; i < domains.length; i++) {
        const domain = domains[i].toLowerCase()
        domainToCustomization.set(domain, {
          displayName: custom.displayName,
          domains: custom.domains,
          id: custom.id,
        })
        if (i > 0) {
          secondaryDomains.add(domain)
        }
      }
    }

    return { domainToCustomization, secondaryDomains }
  }, [trackerCustomizations])

  // Process trackers to apply customizations (nicknames and merged domains)
  // Also includes trackers from the current workflow being edited, so they remain
  // visible even if no torrents currently use them
  const trackerOptions: Option[] = useMemo(() => {
    const { domainToCustomization, secondaryDomains } = trackerCustomizationMaps
    const trackers = trackersQuery.data ? Object.keys(trackersQuery.data) : []
    const processed: Option[] = []
    const seenDisplayNames = new Set<string>()
    const seenValues = new Set<string>()

    // Helper to add a tracker option
    const addTracker = (tracker: string) => {
      const lowerTracker = tracker.toLowerCase()

      if (secondaryDomains.has(lowerTracker)) {
        return
      }

      const customization = domainToCustomization.get(lowerTracker)

      if (customization) {
        const displayKey = customization.displayName.toLowerCase()
        const mergedValue = customization.domains.join(",")
        if (seenDisplayNames.has(displayKey) || seenValues.has(mergedValue)) return
        seenDisplayNames.add(displayKey)
        seenValues.add(mergedValue)

        const primaryDomain = customization.domains[0]
        processed.push({
          label: customization.displayName,
          value: mergedValue,
          icon: <TrackerIconImage tracker={primaryDomain} trackerIcons={trackerIcons} />,
        })
      } else {
        if (seenDisplayNames.has(lowerTracker) || seenValues.has(tracker)) return
        seenDisplayNames.add(lowerTracker)
        seenValues.add(tracker)

        processed.push({
          label: tracker,
          value: tracker,
          icon: <TrackerIconImage tracker={tracker} trackerIcons={trackerIcons} />,
        })
      }
    }

    // Add trackers from current torrents
    for (const tracker of trackers) {
      addTracker(tracker)
    }

    // Add trackers from the workflow being edited (so they persist even if no torrents use them)
    if (rule && rule.trackerPattern !== "*") {
      const savedDomains = parseTrackerDomains(rule)
      for (const domain of savedDomains) {
        addTracker(domain)
      }
    }

    processed.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }))

    return processed
  }, [trackersQuery.data, trackerCustomizationMaps, trackerIcons, rule])

  // Map individual domains to merged option values
  const mapDomainsToOptionValues = useMemo(() => {
    const { domainToCustomization } = trackerCustomizationMaps
    return (domains: string[]): string[] => {
      const result: string[] = []
      const processed = new Set<string>()

      for (const domain of domains) {
        const lowerDomain = domain.toLowerCase()
        if (processed.has(lowerDomain)) continue

        const customization = domainToCustomization.get(lowerDomain)
        if (customization) {
          const mergedValue = customization.domains.join(",")
          if (!result.includes(mergedValue)) {
            result.push(mergedValue)
          }
          for (const d of customization.domains) {
            processed.add(d.toLowerCase())
          }
        } else {
          result.push(domain)
          processed.add(lowerDomain)
        }
      }

      return result
    }
  }, [trackerCustomizationMaps])

  // Initialize form state when dialog opens or rule changes
  useEffect(() => {
    if (open) {
      if (rule) {
        const isAllTrackers = rule.trackerPattern === "*"
        const rawDomains = isAllTrackers ? [] : parseTrackerDomains(rule)
        const mappedDomains = mapDomainsToOptionValues(rawDomains)

        // Parse existing conditions into form state
        const conditions = rule.conditions
        let actionCondition: RuleCondition | null = null
        let speedLimitsEnabled = false
        let shareLimitsEnabled = false
        let pauseEnabled = false
        let deleteEnabled = false
        let tagEnabled = false
        let categoryEnabled = false
        let exprUploadKiB: number | undefined
        let exprDownloadKiB: number | undefined
        let exprRatioLimit: number | undefined
        let exprSeedingTimeMinutes: number | undefined
        let exprDeleteMode: FormState["exprDeleteMode"] = "deleteWithFilesPreserveCrossSeeds"
        let exprTags: string[] = []
        let exprTagMode: FormState["exprTagMode"] = "full"
        let exprUseTrackerAsTag = false
        let exprUseDisplayName = false
        let exprCategory = ""
        let exprIncludeCrossSeeds = false
        let exprBlockIfCrossSeedInCategories: string[] = []

        if (conditions) {
          // Get condition from any enabled action (they should all be the same)
          actionCondition = conditions.speedLimits?.condition
            ?? conditions.shareLimits?.condition
            ?? conditions.pause?.condition
            ?? conditions.delete?.condition
            ?? conditions.tag?.condition
            ?? conditions.category?.condition
            ?? null

          if (conditions.speedLimits?.enabled) {
            speedLimitsEnabled = true
            exprUploadKiB = conditions.speedLimits.uploadKiB
            exprDownloadKiB = conditions.speedLimits.downloadKiB
            // Infer units from existing values - use MiB/s if divisible by 1024
            if (exprUploadKiB !== undefined && exprUploadKiB > 0) {
              setUploadSpeedUnit(exprUploadKiB % 1024 === 0 ? 1024 : 1)
            }
            if (exprDownloadKiB !== undefined && exprDownloadKiB > 0) {
              setDownloadSpeedUnit(exprDownloadKiB % 1024 === 0 ? 1024 : 1)
            }
          }
          if (conditions.shareLimits?.enabled) {
            shareLimitsEnabled = true
            exprRatioLimit = conditions.shareLimits.ratioLimit
            exprSeedingTimeMinutes = conditions.shareLimits.seedingTimeMinutes
          }
          if (conditions.pause?.enabled) {
            pauseEnabled = true
          }
          if (conditions.delete?.enabled) {
            deleteEnabled = true
            exprDeleteMode = conditions.delete.mode ?? "deleteWithFilesPreserveCrossSeeds"
          }
          if (conditions.tag?.enabled) {
            tagEnabled = true
            exprTags = conditions.tag.tags ?? []
            exprTagMode = conditions.tag.mode ?? "full"
            exprUseTrackerAsTag = conditions.tag.useTrackerAsTag ?? false
            exprUseDisplayName = conditions.tag.useDisplayName ?? false
          }
          if (conditions.category?.enabled) {
            categoryEnabled = true
            exprCategory = conditions.category.category ?? ""
            exprIncludeCrossSeeds = conditions.category.includeCrossSeeds ?? false
            exprBlockIfCrossSeedInCategories = conditions.category.blockIfCrossSeedInCategories ?? []
          }
        }

        setFormState({
          name: rule.name,
          trackerPattern: rule.trackerPattern,
          trackerDomains: mappedDomains,
          applyToAllTrackers: isAllTrackers,
          enabled: rule.enabled,
          sortOrder: rule.sortOrder,
          intervalSeconds: rule.intervalSeconds ?? null,
          actionCondition,
          speedLimitsEnabled,
          shareLimitsEnabled,
          pauseEnabled,
          deleteEnabled,
          tagEnabled,
          categoryEnabled,
          exprUploadKiB,
          exprDownloadKiB,
          exprRatioLimit,
          exprSeedingTimeMinutes,
          exprDeleteMode,
          exprTags,
          exprTagMode,
          exprUseTrackerAsTag,
          exprUseDisplayName,
          exprCategory,
          exprIncludeCrossSeeds,
          exprBlockIfCrossSeedInCategories,
        })
      } else {
        setFormState(emptyFormState)
      }
    }
  }, [open, rule, mapDomainsToOptionValues])

  // Build payload from form state (shared by preview and save)
  const buildPayload = (input: FormState): AutomationInput => {
    const conditions: ActionConditions = { schemaVersion: "1" }

    // Add all enabled actions
    if (input.speedLimitsEnabled) {
      conditions.speedLimits = {
        enabled: true,
        uploadKiB: input.exprUploadKiB,
        downloadKiB: input.exprDownloadKiB,
        condition: input.actionCondition ?? undefined,
      }
    }
    if (input.shareLimitsEnabled) {
      conditions.shareLimits = {
        enabled: true,
        ratioLimit: input.exprRatioLimit,
        seedingTimeMinutes: input.exprSeedingTimeMinutes,
        condition: input.actionCondition ?? undefined,
      }
    }
    if (input.pauseEnabled) {
      conditions.pause = {
        enabled: true,
        condition: input.actionCondition ?? undefined,
      }
    }
    if (input.deleteEnabled) {
      conditions.delete = {
        enabled: true,
        mode: input.exprDeleteMode,
        condition: input.actionCondition ?? undefined,
      }
    }
    if (input.tagEnabled) {
      conditions.tag = {
        enabled: true,
        tags: input.exprTags,
        mode: input.exprTagMode,
        useTrackerAsTag: input.exprUseTrackerAsTag,
        useDisplayName: input.exprUseDisplayName,
        condition: input.actionCondition ?? undefined,
      }
    }
    if (input.categoryEnabled) {
      conditions.category = {
        enabled: true,
        category: input.exprCategory,
        includeCrossSeeds: input.exprIncludeCrossSeeds,
        blockIfCrossSeedInCategories: input.exprBlockIfCrossSeedInCategories,
        condition: input.actionCondition ?? undefined,
      }
    }

    return {
      name: input.name,
      trackerDomains: input.applyToAllTrackers ? [] : input.trackerDomains.filter(Boolean),
      trackerPattern: input.applyToAllTrackers ? "*" : input.trackerDomains.filter(Boolean).join(","),
      enabled: input.enabled,
      sortOrder: input.sortOrder,
      intervalSeconds: input.intervalSeconds,
      conditions,
    }
  }

  // Check if current form state represents a delete or category rule (both need previews)
  const isDeleteRule = formState.deleteEnabled
  const isCategoryRule = formState.categoryEnabled

  // Count enabled actions
  const enabledActionsCount = [
    formState.speedLimitsEnabled,
    formState.shareLimitsEnabled,
    formState.pauseEnabled,
    formState.deleteEnabled,
    formState.tagEnabled,
    formState.categoryEnabled,
  ].filter(Boolean).length

  const previewMutation = useMutation({
    mutationFn: async (input: FormState) => {
      const payload = {
        ...buildPayload(input),
        previewLimit: previewPageSize,
        previewOffset: 0,
      }
      return api.previewAutomation(instanceId, payload)
    },
    onSuccess: (result, input) => {
      // Last warning before enabling a delete rule (even if 0 matches right now).
      setPreviewInput(input)
      setPreviewResult(result)
      setShowConfirmDialog(true)
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Failed to preview rule")
    },
  })

  const loadMorePreview = useMutation({
    mutationFn: async () => {
      if (!previewInput || !previewResult) {
        throw new Error("Preview data not available")
      }
      const payload = {
        ...buildPayload(previewInput),
        previewLimit: previewPageSize,
        previewOffset: previewResult.examples.length,
      }
      return api.previewAutomation(instanceId, payload)
    },
    onSuccess: (result) => {
      setPreviewResult(prev => prev? { ...prev, examples: [...prev.examples, ...result.examples], totalMatches: result.totalMatches }: result
      )
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Failed to load more previews")
    },
  })

  const handleLoadMore = () => {
    if (!previewInput || !previewResult) {
      return
    }
    loadMorePreview.mutate()
  }

  const createOrUpdate = useMutation({
    mutationFn: async (input: FormState) => {
      const payload = buildPayload(input)
      if (rule) {
        return api.updateAutomation(instanceId, rule.id, payload)
      }
      return api.createAutomation(instanceId, payload)
    },
    onSuccess: () => {
      toast.success(`Workflow ${rule ? "updated" : "created"}`)
      setShowConfirmDialog(false)
      setPreviewResult(null)
      setPreviewInput(null)
      onOpenChange(false)
      void queryClient.invalidateQueries({ queryKey: ["automations", instanceId] })
      onSuccess?.()
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Failed to save automation")
    },
  })

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setRegexErrors([]) // Clear previous errors

    if (!formState.name) {
      toast.error("Name is required")
      return
    }
    const selectedTrackers = formState.trackerDomains.filter(Boolean)
    if (!formState.applyToAllTrackers && selectedTrackers.length === 0) {
      toast.error("Select at least one tracker")
      return
    }

    // At least one action must be enabled
    if (enabledActionsCount === 0) {
      toast.error("Enable at least one action")
      return
    }

    // Action-specific validation for enabled actions
    if (formState.speedLimitsEnabled) {
      if (formState.exprUploadKiB === undefined && formState.exprDownloadKiB === undefined) {
        toast.error("Set at least one speed limit")
        return
      }
    }
    if (formState.shareLimitsEnabled) {
      if (formState.exprRatioLimit === undefined && formState.exprSeedingTimeMinutes === undefined) {
        toast.error("Set ratio limit or seeding time")
        return
      }
    }
    if (formState.tagEnabled) {
      if (!formState.exprUseTrackerAsTag && formState.exprTags.length === 0) {
        toast.error("Specify at least one tag or enable 'Use tracker name'")
        return
      }
    }
    if (formState.categoryEnabled) {
      if (!formState.exprCategory) {
        toast.error("Select a category")
        return
      }
    }
    if (formState.deleteEnabled && !formState.actionCondition) {
      toast.error("Delete requires at least one condition")
      return
    }

    // Validate regex patterns before saving (only if enabling the workflow)
    const payload = buildPayload(formState)
    if (formState.enabled) {
      try {
        const validation = await api.validateAutomationRegex(instanceId, payload)
        if (!validation.valid && validation.errors.length > 0) {
          setRegexErrors(validation.errors)
          toast.error("Invalid regex pattern - Go/RE2 does not support Perl features like lookahead/lookbehind")
          return
        }
      } catch {
        // If validation endpoint fails, let the save attempt proceed
        // The backend will still reject invalid regexes
      }
    }

    // For delete and category rules, show preview as a last warning before enabling.
    const needsPreview = (isDeleteRule || isCategoryRule) && formState.enabled
    if (needsPreview) {
      previewMutation.mutate(formState)
    } else {
      createOrUpdate.mutate(formState)
    }
  }

  const handleConfirmSave = () => {
    // Clear the stored value so onOpenChange won't restore it after successful save
    setEnabledBeforePreview(null)
    createOrUpdate.mutate(formState)
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-4xl lg:max-w-5xl max-h-[90dvh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{rule ? "Edit Workflow" : "Add Workflow"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto space-y-3 pr-1">
              {/* Header row: Name + All Trackers toggle */}
              <div className="grid gap-3 lg:grid-cols-[1fr_auto] lg:items-end">
                <div className="space-y-1.5">
                  <Label htmlFor="rule-name">Name</Label>
                  <Input
                    id="rule-name"
                    value={formState.name}
                    onChange={(e) => setFormState(prev => ({ ...prev, name: e.target.value }))}
                    required
                    placeholder="Workflow name"
                    autoComplete="off"
                    data-1p-ignore
                  />
                </div>
                <div className="flex items-center gap-2 rounded-md border px-3 py-2">
                  <Switch
                    id="all-trackers"
                    checked={formState.applyToAllTrackers}
                    onCheckedChange={(checked) => setFormState(prev => ({
                      ...prev,
                      applyToAllTrackers: checked,
                      trackerDomains: checked ? [] : prev.trackerDomains,
                    }))}
                  />
                  <Label htmlFor="all-trackers" className="text-sm cursor-pointer whitespace-nowrap">All trackers</Label>
                </div>
              </div>

              {/* Trackers */}
              {!formState.applyToAllTrackers && (
                <div className="space-y-1.5">
                  <Label>Trackers</Label>
                  <MultiSelect
                    options={trackerOptions}
                    selected={formState.trackerDomains}
                    onChange={(next) => setFormState(prev => ({ ...prev, trackerDomains: next }))}
                    placeholder="Select trackers..."
                    creatable
                    onCreateOption={(value) => setFormState(prev => ({ ...prev, trackerDomains: [...prev.trackerDomains, value] }))}
                    disabled={trackersQuery.isLoading}
                    hideCheckIcon
                  />
                </div>
              )}

              {/* Condition and Action */}
              <div className="space-y-3">
                {/* Query Builder */}
                <div className="space-y-1.5">
                  <Label>Conditions (optional)</Label>
                  <QueryBuilder
                    condition={formState.actionCondition}
                    onChange={(condition) => {
                      setFormState(prev => ({ ...prev, actionCondition: condition }))
                      setRegexErrors([]) // Clear errors when condition changes
                    }}
                    allowEmpty
                    categoryOptions={categoryOptions}
                    hiddenFields={supportsTrackerHealth ? [] : ["IS_UNREGISTERED"]}
                    hiddenStateValues={supportsTrackerHealth ? [] : ["tracker_down"]}
                  />
                  {formState.deleteEnabled && !formState.actionCondition && (
                    <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm">
                      <p className="font-medium text-destructive">Delete requires at least one condition.</p>
                    </div>
                  )}
                  {regexErrors.length > 0 && (
                    <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm">
                      <p className="font-medium text-destructive mb-1">Invalid regex pattern</p>
                      {regexErrors.map((err, i) => (
                        <p key={i} className="text-destructive/80 text-xs">
                          <span className="font-mono">{err.pattern}</span>: {err.message}
                        </p>
                      ))}
                      <p className="text-muted-foreground text-xs mt-2">
                        Go/RE2 does not support Perl features like lookahead (?=), lookbehind (?&lt;=), or negative variants (?!), (?&lt;!).
                      </p>
                    </div>
                  )}
                </div>

                {/* Actions section */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Action</Label>
                    {/* Add action dropdown - only show if Delete is not enabled and there are available actions */}
                    {!formState.deleteEnabled && (() => {
                      const enabledActions = getEnabledActions(formState)
                      const availableActions = COMBINABLE_ACTIONS.filter(a => !enabledActions.includes(a))
                      if (availableActions.length === 0) return null
                      return (
                        <Select
                          value=""
                          onValueChange={(action: ActionType) => {
                            setFormState(prev => ({ ...prev, ...setActionEnabled(action, true) }))
                          }}
                        >
                          <SelectTrigger className="w-fit h-7 text-xs">
                            <Plus className="h-3 w-3 mr-1" />
                            Add action
                          </SelectTrigger>
                          <SelectContent>
                            {availableActions.map(action => (
                              <SelectItem key={action} value={action}>{ACTION_LABELS[action]}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )
                    })()}
                  </div>

                  {/* No actions selected - show selector */}
                  {enabledActionsCount === 0 && (
                    <Select
                      value=""
                      onValueChange={(action: ActionType) => {
                        if (action === "delete") {
                          // Delete is standalone - clear all others and set delete
                          setFormState(prev => ({
                            ...prev,
                            speedLimitsEnabled: false,
                            shareLimitsEnabled: false,
                            pauseEnabled: false,
                            deleteEnabled: true,
                            tagEnabled: false,
                            categoryEnabled: false,
                            // Safety: when selecting delete in "create new" mode, start disabled
                            enabled: !rule ? false : prev.enabled,
                          }))
                        } else {
                          setFormState(prev => ({ ...prev, ...setActionEnabled(action, true) }))
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select an action..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="speedLimits">Speed limits</SelectItem>
                        <SelectItem value="shareLimits">Share limits</SelectItem>
                        <SelectItem value="pause">Pause</SelectItem>
                        <SelectItem value="tag">Tag</SelectItem>
                        <SelectItem value="category">Category</SelectItem>
                        <SelectItem value="delete" className="text-destructive focus:text-destructive">Delete (standalone only)</SelectItem>
                      </SelectContent>
                    </Select>
                  )}

                  {/* Render enabled actions */}
                  <div className="space-y-3">
                    {/* Speed limits */}
                    {formState.speedLimitsEnabled && (
                      <div className="rounded-lg border p-3 space-y-3">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm font-medium">Speed limits</Label>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => setFormState(prev => ({ ...prev, speedLimitsEnabled: false }))}
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Upload limit</Label>
                            <div className="flex gap-1">
                              <Input
                                type="number"
                                min={0}
                                className="w-24"
                                value={formState.exprUploadKiB !== undefined ? formState.exprUploadKiB / uploadSpeedUnit : ""}
                                onChange={(e) => {
                                  const displayValue = e.target.value ? Number(e.target.value) : undefined
                                  setFormState(prev => ({
                                    ...prev,
                                    exprUploadKiB: displayValue !== undefined ? Math.round(displayValue * uploadSpeedUnit) : undefined,
                                  }))
                                }}
                                placeholder="No limit"
                              />
                              <Select
                                value={String(uploadSpeedUnit)}
                                onValueChange={(v) => {
                                  const newUnit = Number(v)
                                  if (formState.exprUploadKiB !== undefined) {
                                    const displayValue = formState.exprUploadKiB / uploadSpeedUnit
                                    setFormState(prev => ({
                                      ...prev,
                                      exprUploadKiB: Math.round(displayValue * newUnit),
                                    }))
                                  }
                                  setUploadSpeedUnit(newUnit)
                                }}
                              >
                                <SelectTrigger className="w-fit">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {SPEED_LIMIT_UNITS.map((u) => (
                                    <SelectItem key={u.value} value={String(u.value)}>{u.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Download limit</Label>
                            <div className="flex gap-1">
                              <Input
                                type="number"
                                min={0}
                                className="w-24"
                                value={formState.exprDownloadKiB !== undefined ? formState.exprDownloadKiB / downloadSpeedUnit : ""}
                                onChange={(e) => {
                                  const displayValue = e.target.value ? Number(e.target.value) : undefined
                                  setFormState(prev => ({
                                    ...prev,
                                    exprDownloadKiB: displayValue !== undefined ? Math.round(displayValue * downloadSpeedUnit) : undefined,
                                  }))
                                }}
                                placeholder="No limit"
                              />
                              <Select
                                value={String(downloadSpeedUnit)}
                                onValueChange={(v) => {
                                  const newUnit = Number(v)
                                  if (formState.exprDownloadKiB !== undefined) {
                                    const displayValue = formState.exprDownloadKiB / downloadSpeedUnit
                                    setFormState(prev => ({
                                      ...prev,
                                      exprDownloadKiB: Math.round(displayValue * newUnit),
                                    }))
                                  }
                                  setDownloadSpeedUnit(newUnit)
                                }}
                              >
                                <SelectTrigger className="w-fit">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {SPEED_LIMIT_UNITS.map((u) => (
                                    <SelectItem key={u.value} value={String(u.value)}>{u.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Share limits */}
                    {formState.shareLimitsEnabled && (
                      <div className="rounded-lg border p-3 space-y-3">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm font-medium">Share limits</Label>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => setFormState(prev => ({ ...prev, shareLimitsEnabled: false }))}
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Ratio limit</Label>
                            <Input
                              type="number"
                              step="0.01"
                              min={0}
                              value={formState.exprRatioLimit ?? ""}
                              onChange={(e) => setFormState(prev => ({ ...prev, exprRatioLimit: e.target.value ? Number(e.target.value) : undefined }))}
                              placeholder="e.g. 2.0"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Seed time (min)</Label>
                            <Input
                              type="number"
                              min={0}
                              value={formState.exprSeedingTimeMinutes ?? ""}
                              onChange={(e) => setFormState(prev => ({ ...prev, exprSeedingTimeMinutes: e.target.value ? Number(e.target.value) : undefined }))}
                              placeholder="e.g. 1440"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Pause */}
                    {formState.pauseEnabled && (
                      <div className="rounded-lg border p-3">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm font-medium">Pause</Label>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => setFormState(prev => ({ ...prev, pauseEnabled: false }))}
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Tag */}
                    {formState.tagEnabled && (
                      <div className="rounded-lg border p-3 space-y-3">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm font-medium">Tag</Label>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => setFormState(prev => ({ ...prev, tagEnabled: false }))}
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-[1fr_auto] gap-3 items-start">
                          {formState.exprUseTrackerAsTag ? (
                            <div className="space-y-1">
                              <Label className="text-xs text-muted-foreground">Tags derived from tracker</Label>
                              <div className="flex items-center gap-2 h-9 px-3 rounded-md border bg-muted/50 text-sm text-muted-foreground">
                                Torrents will be tagged with their tracker name
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-1">
                              <Label className="text-xs">Tags</Label>
                              <Input
                                type="text"
                                value={formState.exprTags.join(", ")}
                                onChange={(e) => {
                                  const tags = e.target.value.split(",").map(t => t.trim()).filter(Boolean)
                                  setFormState(prev => ({ ...prev, exprTags: tags }))
                                }}
                                placeholder="tag1, tag2, ..."
                              />
                            </div>
                          )}
                          <div className="space-y-1">
                            <Label className="text-xs">Mode</Label>
                            <Select
                              value={formState.exprTagMode}
                              onValueChange={(value: FormState["exprTagMode"]) => setFormState(prev => ({ ...prev, exprTagMode: value }))}
                            >
                              <SelectTrigger className="w-[120px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="full">Full sync</SelectItem>
                                <SelectItem value="add">Add only</SelectItem>
                                <SelectItem value="remove">Remove only</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <Switch
                              id="use-tracker-tag"
                              checked={formState.exprUseTrackerAsTag}
                              onCheckedChange={(checked) => setFormState(prev => ({
                                ...prev,
                                exprUseTrackerAsTag: checked,
                                exprUseDisplayName: checked ? prev.exprUseDisplayName : false,
                                exprTags: checked ? [] : prev.exprTags,
                              }))}
                            />
                            <Label htmlFor="use-tracker-tag" className="text-sm cursor-pointer whitespace-nowrap">
                              Use tracker name as tag
                            </Label>
                          </div>
                          {formState.exprUseTrackerAsTag && (
                            <div className="flex items-center gap-2">
                              <Switch
                                id="use-display-name"
                                checked={formState.exprUseDisplayName}
                                onCheckedChange={(checked) => setFormState(prev => ({ ...prev, exprUseDisplayName: checked }))}
                              />
                              <Label htmlFor="use-display-name" className="text-sm cursor-pointer whitespace-nowrap">
                                Use display name
                              </Label>
                              <TooltipProvider delayDuration={150}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      type="button"
                                      className="inline-flex items-center text-muted-foreground hover:text-foreground"
                                      aria-label="About display names"
                                    >
                                      <Info className="h-3.5 w-3.5" />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-[280px]">
                                    <p>Uses friendly names from Tracker Customizations instead of raw domains (e.g., "MyTracker" instead of "tracker.example.com").</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Category */}
                    {formState.categoryEnabled && (
                      <div className="rounded-lg border p-3 space-y-3">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm font-medium">Category</Label>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => setFormState(prev => ({ ...prev, categoryEnabled: false }))}
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Move to category</Label>
                            <Select
                              value={formState.exprCategory}
                              onValueChange={(value) => setFormState(prev => ({ ...prev, exprCategory: value }))}
                            >
                              <SelectTrigger className="w-fit min-w-[160px]">
                                <Folder className="h-3.5 w-3.5 mr-2 text-muted-foreground" />
                                <SelectValue placeholder="Select category" />
                              </SelectTrigger>
                              <SelectContent>
                                {categoryOptions.map(opt => (
                                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          {formState.exprCategory && (
                            <div className="flex items-center gap-2 mt-5">
                              <Switch
                                id="include-crossseeds"
                                checked={formState.exprIncludeCrossSeeds}
                                onCheckedChange={(checked) => setFormState(prev => ({ ...prev, exprIncludeCrossSeeds: checked }))}
                              />
                              <Label htmlFor="include-crossseeds" className="text-sm cursor-pointer whitespace-nowrap">
                                Include affected cross-seeds
                              </Label>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Delete - standalone only */}
                    {formState.deleteEnabled && (
                      <div className="rounded-lg border border-destructive/50 p-3 space-y-3">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm font-medium text-destructive">Delete</Label>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => setFormState(prev => ({ ...prev, deleteEnabled: false }))}
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Mode</Label>
                          <Select
                            value={formState.exprDeleteMode}
                            onValueChange={(value: FormState["exprDeleteMode"]) => setFormState(prev => ({ ...prev, exprDeleteMode: value }))}
                          >
                            <SelectTrigger className="w-fit text-destructive">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="delete" className="text-destructive focus:text-destructive">Remove (keep files)</SelectItem>
                              <SelectItem value="deleteWithFiles" className="text-destructive focus:text-destructive">Remove with files</SelectItem>
                              <SelectItem value="deleteWithFilesPreserveCrossSeeds" className="text-destructive focus:text-destructive">Remove with files (preserve cross-seeds)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {formState.categoryEnabled && (formState.exprIncludeCrossSeeds || formState.exprBlockIfCrossSeedInCategories.length > 0) && (
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-1.5">
                      <Label className="text-xs">Skip if cross-seed exists in categories</Label>
                      <TooltipProvider delayDuration={150}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              className="inline-flex items-center text-muted-foreground hover:text-foreground"
                              aria-label="About skipping when cross-seeds exist"
                            >
                              <Info className="h-3.5 w-3.5" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-[320px]">
                            <p>
                              Useful with *arr import queues: prevents automation from moving the torrents if at least one of them are in the *arr import queue.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <MultiSelect
                      options={categoryOptions}
                      selected={formState.exprBlockIfCrossSeedInCategories}
                      onChange={(next) => setFormState(prev => ({ ...prev, exprBlockIfCrossSeedInCategories: next }))}
                      placeholder="Select categories..."
                      creatable
                      onCreateOption={(value) => setFormState(prev => ({
                        ...prev,
                        exprBlockIfCrossSeedInCategories: [...prev.exprBlockIfCrossSeedInCategories, value],
                      }))}
                    />
                    <p className="text-xs text-muted-foreground">
                      Skips the category change if another torrent pointing at the same on-disk content is already in one of these categories.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between pt-3 border-t mt-3">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    id="rule-enabled"
                    checked={formState.enabled}
                    onCheckedChange={(checked) => {
                      if (checked && isDeleteRule && !formState.actionCondition) {
                        toast.error("Delete requires at least one condition")
                        return
                      }
                      // When enabling a delete or category rule, show preview first
                      if (checked && (isDeleteRule || isCategoryRule)) {
                        setEnabledBeforePreview(formState.enabled)
                        const nextState = { ...formState, enabled: true }
                        setFormState(nextState)
                        previewMutation.mutate(nextState)
                      } else {
                        setFormState(prev => ({ ...prev, enabled: checked }))
                      }
                    }}
                  />
                  <Label htmlFor="rule-enabled" className="text-sm font-normal cursor-pointer">Enabled</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="rule-interval" className="text-sm font-normal text-muted-foreground whitespace-nowrap">Run every</Label>
                  <Select
                    value={formState.intervalSeconds === null ? "default" : String(formState.intervalSeconds)}
                    onValueChange={(value) => {
                      const intervalSeconds = value === "default" ? null : Number(value)
                      setFormState(prev => ({ ...prev, intervalSeconds }))
                    }}
                  >
                    <SelectTrigger id="rule-interval" className="w-fit h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Default (15m)</SelectItem>
                      <SelectItem value="60">1 minute</SelectItem>
                      <SelectItem value="300">5 minutes</SelectItem>
                      <SelectItem value="900">15 minutes</SelectItem>
                      <SelectItem value="1800">30 minutes</SelectItem>
                      <SelectItem value="3600">1 hour</SelectItem>
                      <SelectItem value="7200">2 hours</SelectItem>
                      <SelectItem value="14400">4 hours</SelectItem>
                      <SelectItem value="21600">6 hours</SelectItem>
                      <SelectItem value="43200">12 hours</SelectItem>
                      <SelectItem value="86400">24 hours</SelectItem>
                      {/* Show custom option if current value is non-preset */}
                      {formState.intervalSeconds !== null &&
                        ![60, 300, 900, 1800, 3600, 7200, 14400, 21600, 43200, 86400].includes(formState.intervalSeconds) && (
                        <SelectItem value={String(formState.intervalSeconds)}>
                          Custom ({formState.intervalSeconds}s)
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" size="sm" disabled={createOrUpdate.isPending || previewMutation.isPending}>
                  {(createOrUpdate.isPending || previewMutation.isPending) && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {rule ? "Save" : "Create"}
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <WorkflowPreviewDialog
        open={showConfirmDialog}
        onOpenChange={(open) => {
          if (!open) {
            // Restore enabled state if user cancels the preview
            if (enabledBeforePreview !== null) {
              setFormState(prev => ({ ...prev, enabled: enabledBeforePreview }))
              setEnabledBeforePreview(null)
            }
            setPreviewResult(null)
            setPreviewInput(null)
          }
          setShowConfirmDialog(open)
        }}
        title={
          isDeleteRule? (formState.enabled ? "Confirm Delete Rule" : "Preview Delete Rule"): `Confirm Category Change → ${previewInput?.exprCategory ?? formState.exprCategory}`
        }
        description={
          previewResult && previewResult.totalMatches > 0 ? (
            isDeleteRule ? (
              formState.enabled ? (
                <>
                  <p className="text-destructive font-medium">
                    This rule will affect {previewResult.totalMatches} torrent{previewResult.totalMatches !== 1 ? "s" : ""} that currently match.
                  </p>
                  <p className="text-muted-foreground text-sm">Confirming will save and enable this rule.</p>
                </>
              ) : (
                <>
                  <p className="text-muted-foreground">
                    {previewResult.totalMatches} torrent{previewResult.totalMatches !== 1 ? "s" : ""} would match this rule if enabled.
                  </p>
                  <p className="text-muted-foreground text-sm">Confirming will save this rule.</p>
                </>
              )
            ) : (
              <>
                <p>
                  This rule will move{" "}
                  <strong>{(previewResult.totalMatches) - (previewResult.crossSeedCount ?? 0)}</strong> torrent{((previewResult.totalMatches) - (previewResult.crossSeedCount ?? 0)) !== 1 ? "s" : ""}
                  {previewResult.crossSeedCount ? (
                    <> and <strong>{previewResult.crossSeedCount}</strong> cross-seed{previewResult.crossSeedCount !== 1 ? "s" : ""}</>
                  ) : null}
                  {" "}to category <strong>"{previewInput?.exprCategory ?? formState.exprCategory}"</strong>.
                </p>
                <p className="text-muted-foreground text-sm">Confirming will save and enable this rule.</p>
              </>
            )
          ) : (
            <>
              <p>No torrents currently match this rule.</p>
              <p className="text-muted-foreground text-sm">Confirming will save this rule.</p>
            </>
          )
        }
        preview={previewResult}
        condition={previewInput?.actionCondition ?? formState.actionCondition}
        onConfirm={handleConfirmSave}
        onLoadMore={handleLoadMore}
        isLoadingMore={loadMorePreview.isPending}
        confirmLabel="Save Rule"
        isConfirming={createOrUpdate.isPending}
        destructive={isDeleteRule && formState.enabled}
        warning={isCategoryRule}
      />
    </>
  )
}
